import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  addChips,
  chipsOf,
  dateInputValue,
  datetimeInputValue,
  editorKindFor,
  isFlatList,
  parseNumberInput,
  type PropertyType,
  sameValue,
} from './property-values';

const FIELD_CLASS =
  'w-full min-w-0 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm outline-none hover:border-border focus:border-ring';

interface EditorProps {
  name: string;
  value: unknown;
  autoFocus?: boolean;
  onCommit: (value: unknown) => void;
  onUnchanged: () => void;
}

function useFieldCommit(
  initial: string,
  parse: (text: string) => unknown,
  onCommit: (value: unknown) => void,
  onUnchanged: () => void,
) {
  const [draft, setDraft] = useState(initial);
  const cancelled = useRef(false);
  const focused = useRef(false);
  const base = useRef(initial);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (focused.current && draftRef.current !== base.current) {
      return;
    }
    base.current = initial;
    setDraft(initial);
  }, [initial]);

  return {
    draft,
    setDraft,
    handlers: {
      onFocus: () => {
        focused.current = true;
      },
      onBlur: () => {
        focused.current = false;
        base.current = initial;
        if (cancelled.current) {
          cancelled.current = false;
          setDraft(initial);
          onUnchanged();
          return;
        }
        if (draft === initial) {
          onUnchanged();
        } else {
          onCommit(parse(draft));
        }
      },
      onKeyDown: (
        event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
      ) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.stopPropagation();
          cancelled.current = true;
          event.currentTarget.blur();
        }
      },
    },
  };
}

function TextEditor({ name, value, autoFocus, onCommit, onUnchanged }: EditorProps) {
  const initial = value === null || value === undefined ? '' : String(value);
  const field = useFieldCommit(initial, (text) => text, onCommit, onUnchanged);
  return (
    <input
      aria-label={name}
      value={field.draft}
      autoFocus={autoFocus}
      onChange={(e) => field.setDraft(e.target.value)}
      className={FIELD_CLASS}
      {...field.handlers}
    />
  );
}

function NumberEditor({
  name,
  value,
  autoFocus,
  onCommit,
  onUnchanged,
}: EditorProps) {
  const initial = typeof value === 'number' ? String(value) : '';
  const field = useFieldCommit(initial, parseNumberInput, onCommit, onUnchanged);
  return (
    <input
      aria-label={name}
      type="number"
      inputMode="decimal"
      value={field.draft}
      autoFocus={autoFocus}
      onChange={(e) => field.setDraft(e.target.value)}
      className={FIELD_CLASS}
      {...field.handlers}
    />
  );
}

function DateEditor({
  name,
  value,
  kind,
  autoFocus,
  onCommit,
  onUnchanged,
}: EditorProps & { kind: 'date' | 'datetime' }) {
  const initial =
    kind === 'date' ? dateInputValue(value) : datetimeInputValue(value);
  const field = useFieldCommit(
    initial,
    (text) => (text ? text : null),
    onCommit,
    onUnchanged,
  );
  return (
    <input
      aria-label={name}
      type={kind === 'date' ? 'date' : 'datetime-local'}
      value={field.draft}
      autoFocus={autoFocus}
      onChange={(e) => field.setDraft(e.target.value)}
      className={`${FIELD_CLASS} max-w-[16rem]`}
      {...field.handlers}
    />
  );
}

function CheckboxEditor({ name, value, autoFocus, onCommit }: EditorProps) {
  return (
    <input
      aria-label={name}
      type="checkbox"
      checked={value === true}
      autoFocus={autoFocus}
      onChange={(e) => onCommit(e.target.checked)}
      className="mx-1.5 my-1 size-4 accent-foreground"
    />
  );
}

function Chip({
  label,
  onRemove,
  removeLabel,
}: {
  label: string;
  onRemove?: () => void;
  removeLabel?: string;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      <span className="truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          title={removeLabel}
          onClick={onRemove}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <X size={11} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

function ChipsEditor({
  name,
  type,
  value,
  autoFocus,
  onCommit,
  onUnchanged,
}: EditorProps & { type: PropertyType }) {
  const chips = chipsOf(value, type);
  const [text, setText] = useState('');
  const cancelled = useRef(false);

  function add(raw: string) {
    if (!raw.trim()) {
      return;
    }
    const next = addChips(chips, raw, type);
    if (next.length === chips.length) {
      return;
    }
    setText('');
    onCommit(next);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (
      event.key === 'Enter' ||
      event.key === ',' ||
      (event.key === ' ' && type === 'tags' && text.trim())
    ) {
      event.preventDefault();
      add(text);
    } else if (event.key === 'Backspace' && text === '' && chips.length > 0) {
      onCommit(chips.slice(0, -1));
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      cancelled.current = true;
      setText('');
      event.currentTarget.blur();
    }
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1 px-1">
      {chips.map((chip) => (
        <Chip
          key={chip}
          label={type === 'tags' ? `#${chip}` : chip}
          removeLabel={t.app.properties.removeChip({ chip })}
          onRemove={() => onCommit(chips.filter((item) => item !== chip))}
        />
      ))}
      <input
        aria-label={name}
        value={text}
        autoFocus={autoFocus}
        placeholder={
          type === 'tags'
            ? t.app.properties.addTagPlaceholder
            : t.app.properties.addItemPlaceholder
        }
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (cancelled.current) {
            cancelled.current = false;
            onUnchanged();
            return;
          }
          if (text.trim()) {
            add(text);
          } else {
            onUnchanged();
          }
        }}
        className="min-w-[7rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

function ReadOnlyValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="px-1.5 py-0.5 text-muted-foreground">—</span>;
  }
  if (isFlatList(value)) {
    return (
      <div className="flex flex-wrap gap-1 px-1.5 py-0.5">
        {value.map((item, index) => (
          <Chip key={`${index}:${String(item)}`} label={String(item)} />
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    return (
      <pre className="max-h-40 overflow-auto rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return (
    <span className="break-words px-1.5 py-0.5">{String(value)}</span>
  );
}

export interface PropertyRowProps {
  name: string;
  value: unknown;
  type: PropertyType;
  canEdit: boolean;
  draft?: boolean;
  system?: boolean;
  onCommit: (key: string, value: unknown) => void;
  onRemove: (key: string) => void;
  onCancelDraft: () => void;
}

export function PropertyRow({
  name,
  value,
  type,
  canEdit,
  draft,
  system,
  onCommit,
  onRemove,
  onCancelDraft,
}: PropertyRowProps) {
  const readOnly = !canEdit || Boolean(system);
  const kind = readOnly ? 'readonly' : editorKindFor(type, value);

  const editor: EditorProps = {
    name,
    value,
    autoFocus: draft,
    onCommit: (next) => {
      if (sameValue(next, value)) {
        if (draft) {
          onCancelDraft();
        }
        return;
      }
      onCommit(name, next);
    },
    onUnchanged: () => {
      if (draft) {
        onCancelDraft();
      }
    },
  };

  const typeLabel = t.app.properties.types[type];

  return (
    <div
      role="group"
      aria-label={name}
      data-property={name}
      className="group grid grid-cols-[minmax(5rem,9rem)_minmax(0,1fr)_1.5rem] items-start gap-x-3 py-0.5 text-sm"
    >
      <div
        className="flex min-w-0 items-center gap-1 px-1.5 py-0.5 text-muted-foreground"
        title={system ? name : `${name} · ${typeLabel}`}
      >
        <span className="truncate">{name}</span>
      </div>
      <div className="min-w-0">
        {kind === 'text' && <TextEditor {...editor} />}
        {kind === 'number' && <NumberEditor {...editor} />}
        {kind === 'checkbox' && <CheckboxEditor {...editor} />}
        {(kind === 'date' || kind === 'datetime') && (
          <DateEditor {...editor} kind={kind} />
        )}
        {kind === 'chips' && <ChipsEditor {...editor} type={type} />}
        {kind === 'readonly' && <ReadOnlyValue value={value} />}
      </div>
      <div>
        {canEdit && !system && !draft && (
          <button
            type="button"
            aria-label={t.app.properties.remove({ name })}
            title={t.app.properties.remove({ name })}
            onClick={() => onRemove(name)}
            className="rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 max-md:opacity-100"
          >
            <X size={12} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
